import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { format } from "date-fns";
import { it } from "date-fns/locale/it";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/App";
import { Plus, CalendarDays, Users, Trophy, ArrowRight, Trash2, AlertTriangle } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const DAYS = [
  { key: "monday", label: "Lun" },
  { key: "tuesday", label: "Mar" },
  { key: "wednesday", label: "Mer" },
  { key: "thursday", label: "Gio" },
  { key: "friday", label: "Ven" },
  { key: "saturday", label: "Sab" },
  { key: "sunday", label: "Dom" },
];

const ROUND_KEYS = ["QF", "SF", "F"];
const ROUND_LABELS = { QF: "Quarti di Finale", SF: "Semifinale", F: "Finale" };

export default function Dashboard() {
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [deletingTournament, setDeletingTournament] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  const [name, setName] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [matchDirector, setMatchDirector] = useState("");
  const [chiefUmpire, setChiefUmpire] = useState("");
  const [roundDayPrefs, setRoundDayPrefs] = useState({});
  const [courtsConfig, setCourtsConfig] = useState({
    monday: 2, tuesday: 2, wednesday: 2, thursday: 2, friday: 2, saturday: 2, sunday: 2,
  });

  const fetchTournaments = async () => {
    try {
      const data = await api.get("/tournaments");
      setTournaments(data);
    } catch (e) {
      toast.error("Errore nel caricamento dei tornei");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTournaments(); }, []);

  const createTournament = async () => {
    if (!name || !startDate || !endDate) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }
    try {
      const t = await api.post("/tournaments", {
        name,
        start_date: format(startDate, "yyyy-MM-dd"),
        end_date: format(endDate, "yyyy-MM-dd"),
        courts_config: courtsConfig,
        match_director: matchDirector,
        chief_umpire: chiefUmpire,
        round_day_preferences: roundDayPrefs,
      });
      toast.success("Torneo creato!");
      setShowCreate(false);
      setName("");
      setStartDate(null);
      setEndDate(null);
      setMatchDirector("");
      setChiefUmpire("");
      setRoundDayPrefs({});
      navigate(`/tournament/${t.tournament_id}`);
    } catch (e) {
      const msg = e?.message || e?.detail || "Errore nella creazione del torneo";
      toast.error(msg);
    }
  };

  const deleteTournament = async () => {
    if (!deletingTournament) return;
    try {
      await api.del(`/tournaments/${deletingTournament.tournament_id}`);
      toast.success("Torneo eliminato");
      setDeletingTournament(null);
      fetchTournaments();
    } catch (e) {
      toast.error("Errore nell'eliminazione del torneo");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="dashboard-loading">
        <div className="animate-spin h-8 w-8 border-4 border-green-700 border-t-transparent rounded-full" />
      </div>
    );
  }

  const sub = user?.subscription || {};
  const isSuperAdmin = user?.role === "superadmin";
  const isTrial = !isSuperAdmin && sub.status === "trial";
  const isExpired = !isSuperAdmin && sub.status !== "active" && sub.status !== "trial";
  const trialEnd = sub.trial_end ? new Date(sub.trial_end) : null;
  const trialDaysLeft = trialEnd ? Math.max(0, Math.ceil((trialEnd - new Date()) / (1000 * 60 * 60 * 24))) : 0;
  const trialExpired = isTrial && trialDaysLeft <= 0;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Trial / Expired Banner */}
      {(trialExpired || isExpired) && (
        <div className="bg-red-50 border-2 border-red-200 rounded-sm p-4 flex items-center gap-3" data-testid="trial-expired-banner">
          <AlertTriangle className="h-5 w-5 text-red-600 shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-bold text-red-800">La prova gratuita e' scaduta</p>
            <p className="text-xs text-red-600">I tornei creati non sono piu' modificabili. Sottoscrivi un abbonamento per continuare ad utilizzare la piattaforma.</p>
          </div>
          <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white font-bold uppercase rounded-sm shrink-0" onClick={() => navigate("/subscription")}>
            Abbonati Ora
          </Button>
        </div>
      )}
      {isTrial && !trialExpired && (
        <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 flex items-center gap-3" data-testid="trial-active-banner">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
          <p className="text-sm text-amber-800">
            <span className="font-bold">Periodo di prova:</span> {trialDaysLeft} giorni rimanenti &middot; 1 torneo con max 4 categorie
          </p>
        </div>
      )}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-green-700 text-xs font-bold uppercase tracking-widest mb-1">Dashboard</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold uppercase tracking-tight text-slate-900">
            I Tuoi Tornei
          </h1>
        </div>
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <Button data-testid="create-tournament-btn" className="bg-green-700 hover:bg-green-800 text-white font-bold uppercase tracking-wide rounded-sm gap-2">
              <Plus className="h-4 w-4" /> Crea Torneo
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl font-bold uppercase">Nuovo Torneo</DialogTitle>
            </DialogHeader>
            <div className="space-y-5 pt-2">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wide">Nome Torneo</Label>
                <Input
                  data-testid="tournament-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="es. Torneo Estivo 2026"
                  className="mt-1 rounded-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wide">Data Inizio</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button data-testid="start-date-btn" variant="outline" className="w-full justify-start mt-1 rounded-sm text-left font-normal">
                        <CalendarDays className="mr-2 h-4 w-4 text-slate-400" />
                        {startDate ? format(startDate, "dd/MM/yyyy") : "Seleziona"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={startDate} onSelect={setStartDate} locale={it} />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wide">Data Fine</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button data-testid="end-date-btn" variant="outline" className="w-full justify-start mt-1 rounded-sm text-left font-normal">
                        <CalendarDays className="mr-2 h-4 w-4 text-slate-400" />
                        {endDate ? format(endDate, "dd/MM/yyyy") : "Seleziona"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={endDate} onSelect={setEndDate} locale={it} />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wide">Campi Disponibili per Giorno</Label>
                <div className="grid grid-cols-7 gap-2 mt-2">
                  {DAYS.map((d) => (
                    <div key={d.key} className="text-center">
                      <p className="text-xs text-slate-500 mb-1">{d.label}</p>
                      <Input
                        data-testid={`courts-${d.key}-input`}
                        type="number"
                        min="0"
                        max="20"
                        value={courtsConfig[d.key]}
                        onChange={(e) => setCourtsConfig({ ...courtsConfig, [d.key]: parseInt(e.target.value) || 0 })}
                        className="text-center rounded-sm h-9 text-sm"
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wide">Direttore di Gara</Label>
                  <Input
                    data-testid="match-director-input"
                    value={matchDirector}
                    onChange={(e) => setMatchDirector(e.target.value)}
                    placeholder="Nome e cognome"
                    className="mt-1 rounded-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wide">Giudice Arbitro</Label>
                  <Input
                    data-testid="chief-umpire-input"
                    value={chiefUmpire}
                    onChange={(e) => setChiefUmpire(e.target.value)}
                    placeholder="Nome e cognome"
                    className="mt-1 rounded-sm"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wide">Giorno Preferito per Turno</Label>
                <div className="space-y-2 mt-2">
                  {ROUND_KEYS.map((rk) => (
                    <div key={rk} className="flex items-center gap-3">
                      <span className="text-xs font-semibold text-slate-600 w-32">{ROUND_LABELS[rk]}</span>
                      <Select
                        value={roundDayPrefs[rk] || "any"}
                        onValueChange={(val) => setRoundDayPrefs((prev) => {
                          const next = { ...prev };
                          if (val === "any") delete next[rk]; else next[rk] = val;
                          return next;
                        })}
                      >
                        <SelectTrigger className="rounded-sm h-8 text-xs flex-1" data-testid={`round-day-${rk}`}>
                          <SelectValue placeholder="Qualsiasi giorno" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="any">Qualsiasi giorno</SelectItem>
                          {DAYS.map((d) => <SelectItem key={d.key} value={d.key}>{d.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </div>
              <Button
                data-testid="submit-create-tournament-btn"
                onClick={createTournament}
                className="w-full bg-green-700 hover:bg-green-800 text-white font-bold uppercase tracking-wide rounded-sm"
              >
                Crea Torneo
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {tournaments.length === 0 ? (
        <Card className="border-dashed border-2 border-slate-300 bg-white">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Trophy className="h-12 w-12 text-slate-300 mb-4" />
            <p className="text-slate-500 text-center">Nessun torneo ancora. Crea il tuo primo torneo!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {tournaments.map((t) => (
            <Card
              key={t.tournament_id}
              data-testid={`tournament-card-${t.tournament_id}`}
              className="cursor-pointer hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200 bg-white border-slate-200"
              onClick={() => navigate(`/tournament/${t.tournament_id}`)}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <CardTitle className="font-heading text-xl font-bold uppercase tracking-tight text-slate-900">
                    {t.name}
                  </CardTitle>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      data-testid={`delete-tournament-btn-${t.tournament_id}`}
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-slate-400 hover:text-red-600 hover:bg-red-50"
                      onClick={(e) => { e.stopPropagation(); setDeletingTournament(t); }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                    <ArrowRight className="h-4 w-4 text-slate-400 mt-0.5" />
                  </div>
                </div>
                <p className="text-sm text-slate-500">
                  {t.start_date} &rarr; {t.end_date}
                </p>
              </CardHeader>
              <CardContent className="flex items-center gap-2">
                <Badge variant="secondary" className="rounded-sm text-xs">
                  <Trophy className="h-3 w-3 mr-1" />
                  {t.categories?.length || 0} categorie
                </Badge>
                <Badge variant="secondary" className="rounded-sm text-xs">
                  <Users className="h-3 w-3 mr-1" />
                  {t.total_players || 0} giocatori
                </Badge>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <AlertDialog open={!!deletingTournament} onOpenChange={(open) => { if (!open) setDeletingTournament(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-xl font-bold uppercase">Elimina Torneo</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare il torneo <strong>{deletingTournament?.name}</strong>? Tutti i dati associati (categorie, giocatori, partite, tabelloni) verranno eliminati definitivamente. Questa azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-tournament-btn" className="rounded-sm">Annulla</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-tournament-btn"
              onClick={deleteTournament}
              className="bg-red-600 hover:bg-red-700 text-white font-bold uppercase rounded-sm"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
